import type Centrifuge from "centrifuge";
import { getCentrifuge } from "@/src/lib/centrifuge";
import { unwrapCentrifugoPayload } from "@/src/lib/centrifugoPayload";
import type { MediaCallMessage } from "./types";

type SubscriptionLike = Centrifuge.Subscription & {
  presence?: () => Promise<{ presence?: Record<string, { user?: string | number }> }>;
  removeAllListeners?: () => void;
  remove?: () => void;
};

export type MediaCallMessageHandler = (
  message: MediaCallMessage,
  raw: Centrifuge.PublicationContext | { data: unknown },
) => void;

export class SignalChannel {
  private subscription: SubscriptionLike | null = null;
  private readonly handlers = new Set<MediaCallMessageHandler>();
  private subscribePromise: Promise<void> | null = null;
  private resolveSubscribe: (() => void) | null = null;
  private rejectSubscribe: ((error: Error) => void) | null = null;
  private isSubscribed = false;
  private subscribeError: Error | null = null;

  constructor(
    readonly name: string,
    private readonly owned = true,
  ) {}

  connect(): SubscriptionLike {
    if (this.subscription) return this.subscription;

    const centrifuge = getCentrifuge();
    if (!centrifuge) {
      throw new Error("Centrifugo client is not available");
    }

    this.subscribePromise = new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        reject(new Error(`Timed out subscribing to ${this.name}`));
      }, 10000);

      this.resolveSubscribe = () => {
        window.clearTimeout(timeout);
        resolve();
      };

      this.rejectSubscribe = (error) => {
        window.clearTimeout(timeout);
        reject(error);
      };
    });

    this.subscription = centrifuge.subscribe(this.name, {
      publish: (ctx: Centrifuge.PublicationContext) => {
        const data = unwrapCentrifugoPayload(ctx.data);
        if (!isMessage(data)) return;

        for (const handler of this.handlers) {
          handler(data, ctx);
        }
      },
      subscribe: () => {
        this.isSubscribed = true;
        this.subscribeError = null;
        this.resolveSubscribe?.();
      },
      error: (ctx: Centrifuge.SubscribeErrorContext) => {
        console.error("[MediaCall] subscription error", { channel: this.name, ctx });
        const error = new Error(`Failed subscribing to ${this.name}: ${ctx.message || ctx.code}`);
        this.subscribeError = error;
        this.rejectSubscribe?.(error);
      },
      unsubscribe: () => {},
    }) as SubscriptionLike;

    return this.subscription;
  }

  attach(subscription: SubscriptionLike): void {
    this.subscription = subscription;
  }

  addHandler(handler: MediaCallMessageHandler): () => void {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  async publish(message: MediaCallMessage): Promise<void> {
    const subscription = this.connect();
    await this.waitUntilSubscribed();
    await subscription.publish(message);
  }

  async presence(): Promise<Record<string, { user?: string | number }>> {
    const subscription = this.connect();
    await this.waitUntilSubscribed();
    const data = await subscription.presence?.();
    return data?.presence ?? {};
  }

  async waitUntilSubscribed(): Promise<void> {
    this.connect();
    if (this.isSubscribed) return;
    if (this.subscribeError) throw this.subscribeError;
    if (!this.subscribePromise) throw new Error(`Subscription was not created for ${this.name}`);
    return this.subscribePromise;
  }

  destroy(): void {
    if (!this.subscription || !this.owned) return;

    try {
      this.subscription.unsubscribe();
    } finally {
      this.subscription.removeAllListeners?.();
      this.subscription.remove?.();
      this.subscription = null;
      this.subscribePromise = null;
      this.resolveSubscribe = null;
      this.rejectSubscribe = null;
      this.isSubscribed = false;
      this.subscribeError = null;
      this.handlers.clear();
    }
  }
}

function isMessage(value: unknown): value is MediaCallMessage {
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  return typeof record.event === "string" || typeof record.type === "string";
}
